import { ALL_LNGS_NAMESPACES } from '@ingenico/util-i18n-config';
import { useCurrentUser } from '@ingenico/react-section-utils';
import type { ISectionsCopyLabelCard } from '@ingenico/react-ui/copy-label-card';
import { CopyLabelCard } from '@ingenico/react-ui/copy-label-card';
import { useUserDateTimeFormat } from '@ingenico/react-utils/hooks';
import { isEmpty, isNil, isNumber, isUndefined } from 'lodash-es';
import type { ReactElement } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { DeviceCallScheduling } from '../../domain/models/deviceInfo.model';
import { formatCallWindow } from './tetraCallSettings.util';

export function TetraCallSettingsCard({
  callScheduling,
}: {
  readonly callScheduling?: DeviceCallScheduling;
}): ReactElement | null {
  const { t } = useTranslation([ALL_LNGS_NAMESPACES.LOGICAL_DEVICE]);
  const currentUser = useCurrentUser();

  const { getUserDateTimeFormatWithTimezone } = useUserDateTimeFormat(currentUser.dateTimeFormat);

  const { callFrequencyValue, callWindowValue, retryPeriodValue, nextCallDateRaw } = useMemo<{
    callFrequencyValue: string;
    callWindowValue: string;
    retryPeriodValue: string;
    nextCallDateRaw: string;
  }>(() => {
    const nextCallValues =
      isEmpty(callScheduling?.nextCallDate) ||
      isNil(callScheduling?.nextCallDate) ||
      isUndefined(callScheduling?.nextCallDate)
        ? '-'
        : getUserDateTimeFormatWithTimezone({
            instant: new Date(callScheduling.nextCallDate).toISOString(),
            timezone: 'Etc/UTC',
          });

    return {
      callFrequencyValue: isNumber(callScheduling?.callFrequency)
        ? t('tetraCall.callFrequency', { frequency: callScheduling.callFrequency })
        : ('-' as string),
      callWindowValue: formatCallWindow(callScheduling),
      retryPeriodValue: isNumber(callScheduling?.retryPeriod)
        ? t('tetraCall.retryPeriod', {
            retryPeriod: callScheduling.retryPeriod,
          })
        : ('-' as string),
      nextCallDateRaw: nextCallValues,
    };
  }, [callScheduling, getUserDateTimeFormatWithTimezone, t]);

  const sections = useMemo<ISectionsCopyLabelCard>(() => {
    return {
      title: t('tetraCall.title'),
      content: [
        { label: t('tetraCall.deviceCallInterval'), value: callFrequencyValue },
        { label: t('tetraCall.callWindow'), infoTooltip: t('tetraCall.tooltip.callWindow'), value: callWindowValue },
        { label: t('tetraCall.callRetryPeriod'), value: retryPeriodValue },
        { label: t('tetraCall.nextCall'), infoTooltip: t('tetraCall.tooltip.nextCall'), value: nextCallDateRaw },
      ],
    };
  }, [callFrequencyValue, callWindowValue, nextCallDateRaw, retryPeriodValue, t]);

  if (!callScheduling) {
    return null;
  }

  return <CopyLabelCard sections={[sections]} striped />;
}
